import React from 'react';
import tagsGroupConstants from '@shared/constants/overview/TagsGroupConstants';
import AccordionLabel from '@shared/components/accordions/AccordionLabel';
import { limitNumberOfCharacters, convertAllCapsSnakeCaseToTitleCase } from '@shared/utils/strings';
import { VIEW_TYPE } from '@shared/constants/dashboard/campaign-table-constants';
import { AccordionLabelAndSubLabel } from '@shared/components/accordions/accordion-label';
import { isActive } from '@domain/dashboard/campaigns/campaigns.utils';
import { isBudgetManagerTagGroup } from '@domain/budget-manager/budget-manager.utils';
import { MAX_CHARACTERS_FOR_CAMPAIGNS_NAME } from './segments-modal.constants';

const { hasOwnProperty } = Object.prototype.hasOwnProperty;

export const calculateFilledTagsPercentage = (campaigns, taggedCampaigns) => {
    const totalCampaigns = getAmountCampaigns(campaigns);
    const amountOfTagsFilled = getAmountTaggedCampaigns(campaigns, taggedCampaigns);

    if (!totalCampaigns) return 0;

    return Math.round((amountOfTagsFilled * 100) / totalCampaigns);
};

const getAmountTaggedCampaigns = (campaigns, taggedCampaigns) => Object.values(taggedCampaigns)
    .reduce((acc, tag) => (tag.campaign_id in campaigns?.data ? acc + 1 : acc), 0);

const getAmountCampaigns = campaigns => Object.values(campaigns.data)
    .filter(campaign => !campaign.sublevel_id).length;

const buildLabelForAccordionItem = (
    campaign,
    isFbTaggedByAdSet,
) => {
    const isFacebookAds = campaign.platform === tagsGroupConstants.FACEBOOK_ADS;
    const campaignName = limitNumberOfCharacters(
        campaign.campaign_name || campaign.name,
        MAX_CHARACTERS_FOR_CAMPAIGNS_NAME,
    );

    const isFbAdSet = isFacebookAds && isFbTaggedByAdSet;
    const labels = isFbAdSet
        ? buildLabelForAdSet(campaign, campaignName)
        : buildLabelForCampaign(campaign, campaignName, isFacebookAds);

    return <AccordionLabelAndSubLabel {...labels} />;
};

export const buildLabelForCampaign = (
    campaign,
    campaignName,
) => ({
    label: limitNumberOfCharacters(
        campaignName, MAX_CHARACTERS_FOR_CAMPAIGNS_NAME,
    ),
    firstSubLabel: campaign.bid_strategy_type,
});

const buildLabelForAdSet = (
    campaign,
    campaignName,
) => ({
    label: limitNumberOfCharacters(
        campaign.sublevel_name, MAX_CHARACTERS_FOR_CAMPAIGNS_NAME,
    ),
    firstSubLabel: campaignName,
    secondSubLabel: (campaign.objective)
        && limitNumberOfCharacters(
            convertAllCapsSnakeCaseToTitleCase(campaign.objective),
            MAX_CHARACTERS_FOR_CAMPAIGNS_NAME,
        ),
});

export const formatContentToDisplay = (subitems, isFbTaggedByAdSet, platform) => {
    const subitemsLenght = subitems.length;

    if (isFbTaggedByAdSet && platform !== tagsGroupConstants.GOOGLE_ADS) {
        const formattedAdSetDisplay = subitemsLenght > 1
            ? tagsGroupConstants.AD_SETS : tagsGroupConstants.AD_SET;
        return `${subitemsLenght} ${formattedAdSetDisplay}`;
    }

    const formattedCampaignsDisplay = subitemsLenght > 1
        ? tagsGroupConstants.CAMPAIGNS : tagsGroupConstants.CAMPAIGN;
    return `${subitemsLenght} ${formattedCampaignsDisplay}`;
};

const getCampaignTag = (campaign, taggedCampaigns) => {
    const taggedCampaign = taggedCampaigns[campaign.campaign_id];
    return taggedCampaign?.tags[0];
};

const getPlatformAccordionData = campaigns => campaigns.reduce((acc, campaign) => {
    const { platform } = campaign;
    if (!isActive(campaign) || hasOwnProperty.call(acc, platform)) return acc;
    return {
        ...acc,
        [platform]: {
            id: platform,
            platform,
            label: <AccordionLabel platform={platform} />,
            subitems: [],
            subtitle: '',
        },
    };
}, {});

const convertCampaignToSubItem = (campaign, taggedCampaigns, index) => ({
    value: getCampaignTag(campaign, taggedCampaigns),
    label: buildLabelForAccordionItem(campaign, false),
    spanTitle: campaign.name,
    order: index,
    campaignId: campaign.campaign_id,
    sublevelId: campaign.sublevel_id,
    campaignName: campaign.campaign_name,
    campaign,
});

const getCampaignsAccordionData = (
    platformAccordionData, campaigns, taggedCampaigns,
) => campaigns.reduce((acc, campaign, index) => {
    if (!isActive(campaign)) return acc;
    const { platform } = campaign;
    return {
        ...acc,
        [platform]: {
            ...acc[platform],
            subitems: [
                ...acc[platform]?.subitems,
                convertCampaignToSubItem(campaign, taggedCampaigns, index),
            ],
        },
    };
}, platformAccordionData);


const formatAccordionDataContent = accordionData => Object.values(accordionData)
    .reduce((acc, data) => ({
        ...acc,
        [data.platform]: {
            ...data,
            content: formatContentToDisplay(
                data.subitems, false, data.platform,
            ),
        },
    }), {});

export const getObjectForAccordion = (campaigns, taggedCampaigns) => {
    const platformAccordionData = getPlatformAccordionData(campaigns);
    const accordionData = getCampaignsAccordionData(
        platformAccordionData, campaigns, taggedCampaigns,
    );

    return formatAccordionDataContent(accordionData);
};

const getStandardMetricsVisibility = (metrics) => {
    const { type, amount } = metrics;
    return {
        ...metrics,
        type: { ...type, isVisible: true },
        amount: { ...amount, isVisible: false },
    };
};

const getBudgetManagerMetricsVisibility = (metrics) => {
    const {
        type,
        amount,
        budget_manager_status: budgetStatus,
        bid_manager_status: bidStatus,
        monthly_budget: monthlyBudget,
        daily_budget_cluster: dailyBudget,
    } = metrics;
    return {
        ...metrics,
        type: { ...type, isVisible: false },
        amount: { ...amount, isVisible: true },
        budget_manager_status: {
            ...budgetStatus,
            isVisible: true,
        },
        bid_manager_status: {
            ...bidStatus,
            isVisible: true,
        },
        monthly_budget: {
            ...monthlyBudget,
            isVisible: true,
        },
        daily_budget_cluster: {
            ...dailyBudget,
            isVisible: true,
        },
    };
};

export const checkFieldsForViewType = (metrics, groupSlug) => {
    const { type, amount } = metrics;
    if (VIEW_TYPE.standard === groupSlug) return getStandardMetricsVisibility(metrics);
    if (isBudgetManagerTagGroup(groupSlug)) return getBudgetManagerMetricsVisibility(metrics);

    return {
        ...metrics,
        type: { ...type, isVisible: false },
        amount: { ...amount, isVisible: true },
    };
};
